// Row content is the rendered property values from the customer's Notion
// database and is untrusted; render as text, never markup.

import { isNumber, isString } from "../utils/schemaUtils";

export interface NotionDatabaseRow {
  pageId: string;
  url: string;
  content: string;
}

// A data source of a multi-source database the query did not touch.
export interface NotionDataSourceOption {
  id: string;
  name: string;
}

// totalCount is the query's full match count (can exceed rows.length);
// truncated reports the match set was cut at Notion's row cap.
export interface NotionDatabaseRowsPayload {
  type: "notion_database_rows";
  databaseId: string;
  title: string;
  rows: NotionDatabaseRow[];
  totalCount?: number;
  truncated?: boolean;
  dataSources?: NotionDataSourceOption[];
}

const isNotionDatabaseRow = (v: unknown): v is NotionDatabaseRow => {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return isString(r.pageId) && isString(r.url) && isString(r.content);
};

const isNotionDataSourceOption = (v: unknown): v is NotionDataSourceOption => {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return isString(r.id) && isString(r.name);
};

export const parseNotionDatabaseRowsPayload = (v: unknown): NotionDatabaseRowsPayload | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (r.type !== "notion_database_rows") return null;
  if (!isString(r.databaseId)) return null;
  if (!isString(r.title)) return null;
  if (!Array.isArray(r.rows) || !r.rows.every(isNotionDatabaseRow)) return null;
  if (r.dataSources !== undefined && (!Array.isArray(r.dataSources) || !r.dataSources.every(isNotionDataSourceOption)))
    return null;
  return {
    type: "notion_database_rows",
    databaseId: r.databaseId,
    title: r.title,
    rows: r.rows,
    totalCount: isNumber(r.totalCount) ? r.totalCount : undefined,
    truncated: r.truncated === true,
    dataSources: r.dataSources,
  };
};
